import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import Icon, { IconType } from 'components/UI/Icon';
import Button from 'components/UI/Button';

import {
  Form,
  Input,
  InputField,
  ButtonGrid,
  InputIcon,
  InputButton,
} from './styled';

type Props = {
  onSearch: (value: string) => void;
  onClear?: () => void;
  children?: ReactNode;
  buttons?: ReactNode;
  placeholder?: string;
  disable?: boolean;
};

const SearchField: FC<Props> = ({
  onSearch,
  onClear,
  children,
  buttons,
  placeholder = '',
  disable,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    resetField,
    formState: { isDirty },
  } = useForm();

  // Watch
  const search = watch('search');

  // Clear if empty
  useEffect(() => {
    if (onClear && isDirty && !search?.length) {
      onClear();
    }
  }, [onClear, isDirty, search]);

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values) => {
      const value = values.search.trim();
      setValue('search', value);
      onSearch(value);
    },
    [onSearch, setValue]
  );

  // Reset
  const onReset = useCallback(
    () => resetField('search', { keepDirty: true }),
    [resetField]
  );

  // Clear button
  const clearButton = useMemo(() => {
    if (search?.length) {
      return (
        <InputButton role="button" onClick={onReset}>
          <Icon type={IconType.Close} themeType="grey6" size="small" />
        </InputButton>
      );
    }
  }, [onReset, search]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <InputField>
        <InputIcon>
          <Icon type={IconType.Search} themeType="grey6" />
        </InputIcon>
        <Input
          {...register('search', { required: true })}
          placeholder={placeholder}
        />
        {clearButton}
      </InputField>
      {children}
      <ButtonGrid>
        {buttons}
        <Button submit disabled={disable}>
          <FormattedMessage id="search.btn" />
        </Button>
      </ButtonGrid>
    </Form>
  );
};

export default SearchField;
